import { TrackingProvider } from '@act/react-components/tracking/TrackingContext';
import { Permission } from '@act/shared/authorization';
import { CallDto, UserDto } from '@act/shared/data-transfer-objects';
import { generateDatadogTrackingProps } from '@act/toolkit/environment/environment/user-monitoring';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import { Menu, MenuItem } from '@mui/material';
import { useContext, useEffect } from 'react';
import useConfirmationAlertDialog from 'react-components/dialog-forms/hooks/use-confirmation-alert-dialog';
import useInteractionsDialog from 'react-components/dialog-forms/hooks/use-interactions-dialog';
import useAssignCallAuditorDialog from 'react-components/form-dialog/hooks/use-assign-call-auditor-dialog';
import useAssignPhoneCallDialog from 'react-components/form-dialog/hooks/use-assign-phone-call-dialog';
import useAssignPhoneNumberDialog from 'react-components/form-dialog/hooks/use-assign-phone-number-dialog';
import PostCallSummaryContext from 'react-components/member-page/post-call-summary-drawer/PostCallSummaryContext';
import { callsRequests } from 'redux/requests/calls.request';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice';
import { updateCallReview } from 'redux/slices/callsSlice';
import { membersAppSelectors } from 'redux/slices/memberSlice';
import { selectMember } from 'redux/slices/selectedMemberSlice';
import { useAppDispatch } from 'redux/store-hooks';
import { makeRequest } from 'redux/utils/api-request';
import { useFeatureFlag } from 'redux/utils/feature-flags/feature-flag-hook';

interface CallsActionMenuProps {
  anchorEl?: Element;
  open: boolean;
  onClose: () => void;
  call: CallDto;
  actingUser: UserDto;
}

const CallsActionMenu = ({
  anchorEl,
  open,
  onClose,
  call,
  actingUser,
}: CallsActionMenuProps) => {
  const dispatch = useAppDispatch();
  const openAssignPhoneCallDialog = useAssignPhoneCallDialog();
  const openAssignPhoneNumberDialog = useAssignPhoneNumberDialog();
  const openAssignCallAuditorDialog = useAssignCallAuditorDialog();
  const openConfirmReturnCallDialog = useConfirmationAlertDialog();
  const interactionsDialogHelper = useInteractionsDialog();
  const { setVoiceCallId } = useContext(PostCallSummaryContext);

  // Permission checks
  const callDataQualityFF = useFeatureFlag('call-data-quality');
  const canAssignCallReview = actingUserAppSelectors.useUserHasPermission(
    Permission.CAN_ASSIGN_CALL_REVIEWS
  );

  const user = membersAppSelectors.useSelectMemberById(
    call?.incoming ? call?.originator?.userId : call?.receiver?.userId
  );
  useEffect(() => {
    if (user) {
      dispatch(selectMember(user?.id));
    }
  }, [user]);
  const externalPhoneNumber = call?.incoming
    ? call?.originator?.phoneNumber
    : call?.receiver?.phoneNumber;
  const isUnknownIncomingCaller = call?.incoming && !call?.originator?.userId;
  const isIncomingFeedbackCall = call?.incoming && call?.isFeedbackCall;
  const canCallBackFromMenu = isUnknownIncomingCaller || isIncomingFeedbackCall;

  const reviewTask = call?.voiceCallAudits?.find(
    task =>
      Number(task.callId) === call?.id &&
      task.assigneeId === actingUser.id &&
      !task.actionTaken &&
      !task.completedAt
  );

  const createReturnCall = async (callToReturn: CallDto, subject: UserDto) => {
    const phoneNumber = callToReturn?.incoming
      ? callToReturn?.originator?.phoneNumber
      : callToReturn?.receiver?.phoneNumber;
    const isUnknown = callToReturn?.incoming
      ? !callToReturn?.originator?.userId
      : !callToReturn?.receiver?.userId;

    let confirmationTitle: string;
    let confirmationMessage: string;
    if (isUnknown) {
      confirmationTitle = `Return unknown${
        callToReturn.isFeedbackCall ? ' feedback' : ''
      } call`;
      confirmationMessage = `Do you want to return a phone call to ${phoneNumber}?`;
    } else {
      confirmationTitle = `Return${
        callToReturn.isFeedbackCall ? ' feedback' : ''
      } call to ${subject?.name}`;
      confirmationMessage = `Do you want to return a phone call to ${phoneNumber}?`;
    }

    const shouldReturnCall = await openConfirmReturnCallDialog({
      title: confirmationTitle,
      message: confirmationMessage,
      icon: <LocalPhoneIcon />,
    });
    if (shouldReturnCall) {
      if (isUnknown) {
        makeRequest(callsRequests.createCall, {
          params: null,
          body: {
            targetUserId: subject?.id,
            phoneNumber,
          },
        });
      } else {
        const returnedCall = await makeRequest(callsRequests.returnCall, {
          params: null,
          body: {
            callId: callToReturn.id,
          },
        });
        interactionsDialogHelper.editCall({
          user: subject,
          call: returnedCall,
        });
      }
    }
  };
  return (
    <TrackingProvider contextName="calls-action-menu">
      <Menu anchorEl={anchorEl} open={open} onClose={onClose}>
        <MenuItem
          {...generateDatadogTrackingProps('assign-call-button')}
          onClick={async () => {
            void openAssignPhoneCallDialog({
              call: call,
            });
            onClose();
          }}
        >
          Assign call (one time)
        </MenuItem>
        <MenuItem
          {...generateDatadogTrackingProps('assign-phone-number-button')}
          onClick={async () => {
            void openAssignPhoneNumberDialog({
              phoneNumber: externalPhoneNumber,
              callId: call.id,
            });
            onClose();
          }}
        >
          Assign phone number
        </MenuItem>
        {canCallBackFromMenu && (
          <MenuItem
            {...generateDatadogTrackingProps('call-back-button')}
            onClick={async () => {
              onClose();
              createReturnCall(call, user);
            }}
          >
            Call back
          </MenuItem>
        )}
        {canAssignCallReview && call?.subjectId === null && (
          <MenuItem
            {...generateDatadogTrackingProps('share-call-button')}
            onClick={() => {
              void openAssignCallAuditorDialog({
                call: call,
                callsReviewTasks: call?.voiceCallAudits,
              });
              onClose();
            }}
          >
            Share call
          </MenuItem>
        )}
        {/* Show todo-related menu items based on existing todos */}
        {reviewTask && (
          <MenuItem
            {...generateDatadogTrackingProps('no-action-required-button')}
            onClick={async () => {
              // Find the specific review task for this call and user
              if (reviewTask) {
                await dispatch(
                  updateCallReview({
                    params: { auditId: reviewTask.id },
                    body: { actionTaken: false },
                  })
                );
              }
              onClose();
            }}
          >
            No action required
          </MenuItem>
        )}
        {callDataQualityFF && (
          <MenuItem
            {...generateDatadogTrackingProps('view-summary-button')}
            onClick={() => {
              setVoiceCallId(call.id);
              onClose();
            }}
          >
            View summary
          </MenuItem>
        )}
      </Menu>
    </TrackingProvider>
  );
};

export default CallsActionMenu;
